// ALTIORA Radar · Sprint 9E + Sprint 10 (MarketProfile)
// - Dashboard fondateur (5 colonnes)
// - Opportunités re-classées selon le MarketProfile de l'utilisateur
// - Boutons feedback (Pertinent / Non pertinent / À suivre / Contacté / Converti)
// - Bouton « Configurer mon marché » ouvrant MarketProfileOnboarding
import { useEffect, useState } from "react";
import { RefreshCw, ExternalLink, ShieldCheck, AlertTriangle,
          TrendingUp, Sliders, Check, X, Clock, Phone, Trophy, Star } from "lucide-react";
import { api } from "@/lib/api";
import { NAVY, GOLD, SLATE, CHAMPAGNE } from
  "@/components/altiora/PremiumUI";
import MarketProfileOnboarding from
  "@/components/altiora/MarketProfileOnboarding";

const CAT_COLORS = {
  DECISION_MAKER: { bg: "#D8F5E5", color: "#0F5E39", label: "Décideur" },
  INDIVIDUAL_PROSPECT: { bg: "#E9F0FA", color: "#1E3A70", label: "Prospect ind." },
  PARTNER: { bg: "#FDECC8", color: "#8A5200", label: "Partenaire" },
  REFERRAL_SOURCE: { bg: "#F2E7FA", color: "#5B247A", label: "Référencement" },
  LOW_RELEVANCE: { bg: "#E5E7EB", color: "#64748B", label: "Faible" },
  OTHER: { bg: "#F5F5F5", color: "#8A8A8A", label: "Non classé" },
};

const VERDICT_BUTTONS = [
  { id: "RELEVANT", label: "Pertinent", icon: Check, color: "#0F5E39" },
  { id: "NOT_RELEVANT", label: "Non pertinent", icon: X, color: "#B91C1C" },
  { id: "TO_FOLLOW", label: "À suivre", icon: Clock, color: "#5B247A" },
  { id: "CONTACTED", label: "Contacté", icon: Phone, color: "#1E3A70" },
  { id: "CONVERTED", label: "Converti", icon: Trophy, color: "#8A5200" },
];

function TriLevel({ chan, data }) {
  const { found, verified, authorized, found_value: v } = data || {};
  return (
    <div data-testid={`coord-${chan}`} className="text-[11px] space-y-0.5">
      <div className="font-mono uppercase tracking-[0.16em]" style={{ color: SLATE }}>{chan}</div>
      <div className="flex gap-1.5 items-center">
        <span className={`px-1.5 py-0.5 rounded ${found ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"}`}>{found ? "trouvé" : "—"}</span>
        <span className={`px-1.5 py-0.5 rounded ${verified ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-500"}`}>{verified ? "vérifié" : "—"}</span>
        <span className={`px-1.5 py-0.5 rounded ${authorized ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-500"}`}>{authorized ? "autorisé" : "—"}</span>
      </div>
      {found && authorized && v && (<div className="font-mono text-[10px]" style={{ color: NAVY }}>{v}</div>)}
      {found && !authorized && (<div className="text-[10px] italic" style={{ color: SLATE }}>Coordonnée trouvée · non-autorisée (LNNTE/suppression non vérifiés)</div>)}
    </div>
  );
}

function FeedbackBar({ opp, onDone }) {
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  async function submit(verdict) {
    setBusy(true);
    try {
      await api.post("/leads-engine/market-profile/feedback", {
        entity_id: opp.entity_id,
        verdict,
        matched_segments: opp.profile_match?.matched_segments || [],
        source_family: opp.source_family,
        signal_type: opp.signal_type_raw,
      });
      setSent(verdict);
      onDone?.(verdict);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex flex-wrap gap-1" data-testid={`feedback-bar-${opp.entity_id}`}>
      {VERDICT_BUTTONS.map((v) => {
        const Icon = v.icon;
        const active = sent === v.id;
        return (
          <button key={v.id}
                  onClick={() => submit(v.id)}
                  disabled={busy || sent}
                  data-testid={`feedback-${v.id.toLowerCase()}-${opp.entity_id}`}
                  className="text-[10px] px-1.5 py-1 rounded border flex items-center gap-1"
                  style={{
                    background: active ? v.color : "white",
                    color: active ? "white" : v.color,
                    borderColor: v.color,
                    opacity: (sent && !active) ? 0.4 : 1,
                    cursor: (busy || sent) ? "default" : "pointer",
                  }}>
            <Icon className="h-2.5 w-2.5" />{v.label}
          </button>
        );
      })}
    </div>
  );
}

export default function AltioraRadar() {
  const [data, setData] = useState(null);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minRelevance, setMinRelevance] = useState(60);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [applyProfile, setApplyProfile] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const [today, setToday] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [opps, radar, todayOpps] = await Promise.all([
        api.get("/leads-engine/radar/opportunities", { params: {
          min_relevance: minRelevance,
          category: categoryFilter || undefined,
          apply_profile: applyProfile,
          limit: 100 }}),
        api.get("/leads-engine/radar/dashboard"),
        api.get("/leads-engine/opportunities/today", { params: { limit: 5 }})
          .catch(() => ({ data: { items: [] }})),
      ]);
      setData(opps.data); setDash(radar.data);
      setToday(todayOpps.data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [minRelevance, categoryFilter, applyProfile]);

  const profileSummary = data?.profile_summary;
  const isCustomProfile = !!data?.profile_is_custom;

  return (
    <div data-testid="altiora-radar-page" className="min-h-screen" style={{ background: "#FBF6EA" }}>
      <header className="px-8 py-6" style={{ background: NAVY, color: "#FBF6EA" }}>
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-[0.24em]" style={{ color: GOLD }}>ALTIORA · Radar Québec</span>
          <button onClick={() => setProfileOpen(true)}
                  data-testid="open-market-profile"
                  className="ml-auto text-[11px] flex items-center gap-1 px-2.5 py-1 rounded"
                  style={{ background: `${GOLD}33`, color: "#FBF6EA" }}>
            <Sliders className="h-3 w-3" /> Configurer mon marché
          </button>
          <button onClick={load} data-testid="radar-refresh"
                  className="text-[11px] flex items-center gap-1 px-2 py-1 rounded"
                  style={{ background: `${GOLD}33` }}><RefreshCw className="h-3 w-3" /> Rafraîchir</button>
        </div>
        <h1 className="font-serif text-3xl mt-2">Opportunités du jour</h1>
        <p className="text-[12px] mt-1" style={{ color: GOLD }}>{dash?.principal_kpi}</p>
        {profileSummary && (
          <div className="text-[11px] mt-2" style={{ color: "#FBF6EA" }}
                data-testid="market-profile-active-summary">
            {isCustomProfile ? "Profil marché actif : " : "Profil par défaut (100 % marché général) : "}
            {Object.entries(profileSummary.weights).map(([k, v]) =>
              `${k} ${v}%`).join(" · ")}
            {" "}· Territoires : {profileSummary.territories.join(", ")}
            {profileSummary.exclusions?.length > 0 && (
              <> · Exclusions : {profileSummary.exclusions.length}</>)}
          </div>
        )}
      </header>
      {dash && (
        <div className="px-8 py-4 grid grid-cols-5 gap-3" data-testid="radar-columns">
          {[["signaux_detectes","Signaux détectés"],["entreprises_qualifiees","Entreprises qualifiées"],["a_enrichir","À enrichir"],["leads_exploitables","Leads exploitables"],["a_contacter","À contacter"]].map(([k,label]) => (
            <div key={k} data-testid={`radar-col-${k}`} className="rounded-lg p-3 bg-white" style={{ border: `1px solid ${CHAMPAGNE}55` }}>
              <div className="text-[10px] font-mono uppercase tracking-[0.16em]" style={{ color: SLATE }}>{label}</div>
              <div className="text-2xl font-serif mt-1" style={{ color: NAVY }}>{dash.columns[k]}</div>
            </div>
          ))}
        </div>
      )}
      {/* À traiter aujourd'hui — Sprint 11 P2 */}
      {today?.items?.length > 0 && (
        <div className="px-8 pt-2 pb-3" data-testid="today-actionable">
          <div className="rounded-xl p-4 bg-white" style={{ border: `2px solid ${GOLD}` }}>
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.24em]" style={{ color: GOLD }}>
              <Star className="h-3 w-3" /> À traiter aujourd'hui · {today.items.length} opportunité{today.items.length>1?"s":""} actionnable{today.items.length>1?"s":""}
            </div>
            <div className="mt-3 space-y-2">
              {today.items.map((o, idx) => (
                <div key={o.opportunity_id} data-testid={`today-opp-${idx}`}
                      className="rounded p-3 grid gap-2"
                      style={{ background: "#FBF6EA", gridTemplateColumns: "1.6fr 1fr 0.5fr" }}>
                  <div>
                    <div className="font-serif text-[14px]" style={{ color: NAVY }}>
                      {o.person_snapshot.full_name}
                    </div>
                    <div className="text-[11px]" style={{ color: SLATE }}>
                      {o.person_snapshot.title || "—"} {o.person_snapshot.organization ? "· " + o.person_snapshot.organization : ""}
                    </div>
                    <div className="text-[10px] mt-1 font-mono uppercase tracking-[0.14em]" style={{ color: GOLD }}>
                      {o.signal?.signal_type || "—"} · {o.signal_source_id} · {o.signal_date?.slice(0,10) || ""}
                    </div>
                    <div className="text-[10px] italic mt-1" style={{ color: SLATE }} data-testid={`today-action-${idx}`}>
                      Action : {o.next_action}
                    </div>
                  </div>
                  <div className="text-[10px]" style={{ color: SLATE }}>
                    <div className="uppercase font-mono tracking-[0.14em]">Facteurs</div>
                    {(o.score_factors || []).slice(0,4).map((f, i) => {
                      const [k, v] = Object.entries(f)[0];
                      return <div key={i}>{k} · {v}</div>;
                    })}
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-serif" style={{ color: NAVY }}>{o.score}</div>
                    <div className="text-[9px]" style={{ color: SLATE }}>score explicable</div>
                    <div className="text-[9px] mt-1" style={{ color: o.contactable ? "#0F5E39" : "#B91C1C" }}>
                      {o.contactable ? "contactable" : "à autoriser"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="px-8 py-3 flex items-center gap-3 flex-wrap">
        <label className="text-[11px] flex items-center gap-2" style={{ color: SLATE }}>Score min :
          <input type="number" value={minRelevance} min={0} max={100} onChange={(e) => setMinRelevance(Number(e.target.value))} data-testid="radar-min-relevance" className="w-14 rounded px-1.5 py-0.5 border" /></label>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} data-testid="radar-category-filter" className="text-[11px] rounded px-2 py-1 border">
          <option value="">Toutes catégories</option>
          <option value="DECISION_MAKER">Décideur</option>
          <option value="INDIVIDUAL_PROSPECT">Prospect individuel</option>
          <option value="PARTNER">Partenaire</option>
          <option value="REFERRAL_SOURCE">Référencement</option>
        </select>
        <label className="text-[11px] flex items-center gap-1.5" style={{ color: SLATE }}>
          <input type="checkbox" checked={applyProfile}
                 onChange={(e) => setApplyProfile(e.target.checked)}
                 data-testid="toggle-apply-profile" />
          Appliquer mon profil marché
        </label>
        <span className="text-[11px] ml-auto" style={{ color: SLATE }}>{data?.count || 0} opportunité(s)</span>
      </div>
      <div className="px-8 pb-10 space-y-3">
        {loading && <div style={{ color: SLATE }}>Chargement…</div>}
        {!loading && (data?.opportunities || []).map((o, idx) => {
          const cat = CAT_COLORS[o.person?.category] || CAT_COLORS.OTHER;
          const adjusted = o.opportunity_relevance_adjusted;
          const bonus = o.profile_bonus || 0;
          const personal = o.personal_learning_bonus || 0;
          return (
            <article key={o.entity_id || idx} data-testid={`opportunity-${idx}`} className="rounded-xl bg-white p-4 grid gap-3" style={{ border: `1px solid ${CHAMPAGNE}55`, gridTemplateColumns: "1.4fr 1fr 1.2fr 0.6fr" }}>
              <div>
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.16em]" style={{ color: GOLD }}>
                  <TrendingUp className="h-3 w-3" /> {o.signal_label}<span style={{ color: SLATE }}>· {o.signal_date || "—"} · {o.source}</span>
                </div>
                <h3 className="font-serif text-[16px] mt-1" style={{ color: NAVY }}>{o.entity_name}</h3>
                {o.trade_name && o.trade_name !== o.entity_name && (<div className="text-[10px]" style={{ color: SLATE }}>Nom légal : {o.legal_name}</div>)}
                {o.domain && (<a href={o.domain?.startsWith("http") ? o.domain : `https://${o.domain}`} target="_blank" rel="noreferrer" className="text-[11px] mt-1 flex items-center gap-1" style={{ color: NAVY }}><ExternalLink className="h-3 w-3" /> {o.domain}</a>)}
                {o.profile_match && (
                  <div className="text-[10px] mt-2 italic" style={{ color: SLATE }}
                        data-testid={`profile-match-${idx}`}>
                    {o.profile_match.reason}
                  </div>
                )}
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.14em]" style={{ color: SLATE }}>Personne</div>
                <div className="font-serif text-[15px]" style={{ color: NAVY }}>{o.person?.name || "— aucune personne identifiée —"}</div>
                <div className="text-[11px]" style={{ color: SLATE }}>{o.person?.role || ""}</div>
                <span className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase" style={{ background: cat.bg, color: cat.color }} data-testid="opportunity-category">{cat.label}</span>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.14em]" style={{ color: SLATE }}>Coordonnées (strict)</div>
                <div className="space-y-1 mt-1">
                  <TriLevel chan="email" data={o.coordinate?.email} />
                  <TriLevel chan="phone" data={o.coordinate?.phone} />
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] font-mono uppercase tracking-[0.14em]" style={{ color: SLATE }}>Pertinence</div>
                <div className="text-3xl font-serif" style={{ color: NAVY }}
                      data-testid={`opp-score-${idx}`}>{adjusted ?? o.opportunity_relevance}</div>
                {(bonus !== 0 || personal !== 0) && (
                  <div className="text-[9px] mt-0.5" style={{ color: SLATE }}
                        data-testid={`opp-bonus-${idx}`}>
                    Brut {o.opportunity_relevance}
                    {bonus !== 0 && (<> · Profil {bonus > 0 ? "+" : ""}{bonus}</>)}
                    {personal !== 0 && (<> · Apprentissage {personal > 0 ? "+" : ""}{personal}</>)}
                  </div>
                )}
                <div className="text-[9px] mt-1 italic" style={{ color: SLATE }}>{o.next_action}</div>
              </div>
              {o.why_now && (
                <div className="col-span-4 rounded p-2 text-[11px]" style={{ background: "#FBF6EA", border: `1px solid ${CHAMPAGNE}44`, color: NAVY }} data-testid="opportunity-why-now">
                  <div className="flex gap-1 items-start"><ShieldCheck className="h-3 w-3 mt-0.5" style={{ color: GOLD }} />
                  <div><strong>Pourquoi maintenant :</strong> {o.why_now}
                    <div className="text-[10px] italic mt-0.5" style={{ color: SLATE }}>{o.why_now_disclaimer}</div>
                  </div></div>
                </div>
              )}
              <div className="col-span-4 pt-2 border-t"
                    style={{ borderColor: `${CHAMPAGNE}66` }}>
                <FeedbackBar opp={o} onDone={() => { /* mémorisé côté user */ }} />
              </div>
            </article>
          );
        })}
        {!loading && (data?.opportunities || []).length === 0 && (
          <div className="rounded-xl bg-white p-6 text-center" style={{ border: `1px dashed ${CHAMPAGNE}88` }}>
            <AlertTriangle className="h-6 w-6 mx-auto mb-2" style={{ color: GOLD }} />
            <div className="font-serif text-[16px]" style={{ color: NAVY }}>Aucune opportunité au seuil courant.</div>
            <div className="text-[12px] mt-1" style={{ color: SLATE }}>Abaisser le score min ou lancer <code>POST /api/leads-engine/radar/iq/import-to-queue</code></div>
          </div>
        )}
        <div className="text-[10px] italic pt-3 border-t" style={{ color: SLATE, borderColor: `${CHAMPAGNE}55` }}>{data?.disclaimer}</div>
      </div>
      <MarketProfileOnboarding open={profileOpen}
                                onClose={() => setProfileOpen(false)}
                                onSaved={load} />
    </div>
  );
}
